import React, { useState, useEffect } from "react";
import api from "../services/api";
import { useNavigate, useSearchParams, Link } from "react-router-dom";
import NavBar from "../components/navbar/navbar";
import { useTranslation } from "react-i18next";
import "bootstrap/dist/css/bootstrap.min.css";
import "bootstrap-icons/font/bootstrap-icons.css";

export default function ResetPasswordPage() {
  const { t } = useTranslation();
  const [searchParams] = useSearchParams();
  const [newPassword, setNewPassword] = useState("");
  const [confirmPassword, setConfirmPassword] = useState("");
  const [error, setError] = useState("");
  const [success, setSuccess] = useState("");
  const [loading, setLoading] = useState(false);
  const [showPassword, setShowPassword] = useState(false);
  const [showConfirmPassword, setShowConfirmPassword] = useState(false);
  const navigate = useNavigate();
  const token = searchParams.get("token");

  useEffect(() => {
    if (!token) {
      setError(t("reset_password_token_missing"));
    }
  }, [token, t]);

  const handleSubmit = async (e) => {
    e.preventDefault();
    setError("");
    setSuccess("");

    if (!token) {
      setError(t("reset_password_token_missing"));
      return;
    }

    if (newPassword.length < 6) {
      setError(t("password_min_length"));
      return;
    }

    if (newPassword !== confirmPassword) {
      setError(t("passwords_do_not_match"));
      return;
    }

    setLoading(true);

    try {
      const res = await api.post("/auth/reset-password", { 
        token, 
        newPassword 
      });
      setSuccess(res.data.message || "Mot de passe réinitialisé avec succès. Redirection vers la page de connexion...");
      
      // Rediriger vers la page de connexion après 2 secondes
      setTimeout(() => {
        navigate("/login");
      }, 2000);
    } catch (err) {
      console.error(err);
      setError(err.response?.data?.error || t("reset_password_error"));
    } finally {
      setLoading(false);
    }
  };

  return (
    <div>
      <NavBar />

      <div className="min-vh-100 d-flex align-items-center justify-content-center bg-body-secondary">
        <style>{`
          .reset-card { max-width: 420px; border-radius: 15px; }
          .form-control:focus { box-shadow: 0 0 0 0.2rem rgba(13,110,253,.25); }
          .reset-title { font-weight: 600; }
        `}</style>

        <div className="card reset-card shadow p-4 border-0 bg-white w-100">
          <div className="text-center mb-4">
            <i className="bi bi-shield-lock-fill text-primary" style={{ fontSize: "2rem" }} />
            <h3 className="text-primary reset-title mt-2">{t("reset_password_title")}</h3>
            <p className="text-muted">{t("reset_password_subtitle")}</p>
          </div>

          {error && <div className="alert alert-danger">{error}</div>}
          {success && <div className="alert alert-success">{success}</div>}

          {token ? (
            <form onSubmit={handleSubmit}>
              <div className="mb-3">
                <label className="form-label" htmlFor="newPassword">{t("new_password_label")}</label>
                <div className="position-relative">
                  <input
                    id="newPassword"
                    type={showPassword ? "text" : "password"}
                    className="form-control"
                    placeholder={t("new_password_placeholder")}
                    value={newPassword}
                    onChange={(e) => setNewPassword(e.target.value)}
                    required
                    autoComplete="new-password"
                    style={{ paddingRight: "40px" }}
                  />
                  <button
                    type="button"
                    className="btn btn-link position-absolute end-0 top-50 translate-middle-y"
                    style={{ 
                      border: "none", 
                      background: "none", 
                      padding: "0 10px",
                      color: "#6c757d",
                      textDecoration: "none"
                    }}
                    onClick={() => setShowPassword(!showPassword)}
                    aria-label={showPassword ? "Masquer le mot de passe" : "Afficher le mot de passe"}
                  >
                    <i className={`bi ${showPassword ? "bi-eye-slash" : "bi-eye"}`} style={{ fontSize: "1.2rem" }} />
                  </button>
                </div>
              </div>

              <div className="mb-3">
                <label className="form-label" htmlFor="confirmPassword">{t("confirm_password_label")}</label>
                <div className="position-relative">
                  <input
                    id="confirmPassword"
                    type={showConfirmPassword ? "text" : "password"}
                    className="form-control"
                    placeholder={t("confirm_password_placeholder")}
                    value={confirmPassword}
                    onChange={(e) => setConfirmPassword(e.target.value)}
                    required
                    autoComplete="new-password"
                    style={{ paddingRight: "40px" }}
                  />
                  <button
                    type="button"
                    className="btn btn-link position-absolute end-0 top-50 translate-middle-y"
                    style={{ 
                      border: "none", 
                      background: "none", 
                      padding: "0 10px",
                      color: "#6c757d",
                      textDecoration: "none"
                    }}
                    onClick={() => setShowConfirmPassword(!showConfirmPassword)}
                    aria-label={showConfirmPassword ? "Masquer le mot de passe" : "Afficher le mot de passe"}
                  >
                    <i className={`bi ${showConfirmPassword ? "bi-eye-slash" : "bi-eye"}`} style={{ fontSize: "1.2rem" }} />
                  </button>
                </div>
              </div>

              <button 
                type="submit" 
                className="btn btn-primary w-100"
                disabled={loading}
              >
                {loading ? (
                  <>
                    <span className="spinner-border spinner-border-sm me-2" role="status" aria-hidden="true"></span>
                    {t("resetting")}
                  </>
                ) : (
                  <>
                    <i className="bi bi-check-circle me-2" />
                    {t("reset_password_button")}
                  </>
                )}
              </button>
            </form>
          ) : (
            <div className="alert alert-warning">
              {t("reset_password_token_missing")}
            </div>
          )}

          <div className="text-center mt-4">
            <p className="mb-0">
              <Link to="/login" className="text-decoration-none text-primary">
                <i className="bi bi-arrow-left me-1" />
                {t("back_to_login")}
              </Link>
            </p>
          </div>
        </div>
      </div>
    </div>
  );
}

